import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import moment from "moment";

import Stack from "@mui/material/Stack";
import {
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SettingsIcon from "@mui/icons-material/Settings";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import Loader from "@jumbo/components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { userType } from "app/helpers/userType";

import SendNotification from "./send-notification";
import NotificationSettings from "./notification-settings";
import ViewNotification from "./view-notification";
import {
  deleteSupplierNotification,
  getSupplierNotificationList,
  sendSupplierNotification,
  updateNotificationSettings,
} from "app/redux/slice/supplierNotificationSlice";

const NOTIFICATION_TYPES = ["General", "Price Update", "Product", "Offer"];

const Notification = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const notificationData = useSelector((state) => state?.supplierNotification);

  const [searchText, setSearchText] = useState("");
  const [type, setType] = useState("All");
  const [source, setSource] = useState("All");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isSettingsOpen,
    onClose: closeSettings,
    onOpen: openSettings,
  } = useDisclosure();
  const {
    isOpen: isView,
    onClose: closeView,
    actionProps: viewActionProps,
    stageAction: viewStageAction,
  } = useDisclosure();
  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "supplier-notification" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );
  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
      return;
    }
    let permissionValue = {
      createPermission: false,
      updatePermission: false,
      deletePermission: false,
    };
    const rolePermission = permissionData?.filter((item) => {
      return item?.moduleName?.includes("Notification") && item;
    });
    rolePermission?.length &&
      rolePermission.forEach(function (num) {
        if (num?.name === "create-supplier-notification") {
          permissionValue.createPermission = true;
        }
        if (num?.name === "delete-supplier-notification") {
          permissionValue.deletePermission = true;
        }
        if (num?.name === "update-supplier-notification") {
          permissionValue.updatePermission = true;
        }
      });
    setPermission(permissionValue);
  };

  useEffect(() => {
    getPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { id: "title", label: "Title", sort: true },
    {
      id: "type",
      label: "Type",
      render: (row) => <Chip size="small" label={row.type} />,
    },
    {
      id: "productId",
      label: "Product",
      render: (row) => row.productId?.name || "—",
    },
    {
      id: "audience",
      label: "Sent To",
      render: (row) => {
        if (row.audience === "PRODUCT_RATE") {
          return (
            <Tooltip title="Vendors holding a party rate on this product">
              <Chip
                size="small"
                label={`Rated (${row.totalRecipients || 0})`}
                color="success"
              />
            </Tooltip>
          );
        }
        // isGlobal is the older signal and still true for every ALL send, so
        // rows created before `audience` existed keep rendering correctly.
        return row.isGlobal !== false ? (
          <Chip size="small" label="All Vendors" color="info" />
        ) : (
          <Chip
            size="small"
            label={`${row.assignedSuppliers?.length || 0} Vendor(s)`}
            color="secondary"
          />
        );
      },
    },
    {
      id: "readCount",
      label: "Read",
      render: (row) => `${row.readCount || 0} / ${row.totalRecipients || 0}`,
    },
    {
      id: "source",
      label: "Source",
      render: (row) =>
        row.source === "AUTO" ? (
          <Tooltip title={row.eventKey || ""}>
            <Chip size="small" label="Automatic" color="warning" />
          </Tooltip>
        ) : (
          <Chip size="small" label="Manual" />
        ),
    },
    {
      id: "createdBy",
      label: "Sent By",
      render: (row) =>
        row.source === "AUTO" ? "System" : row.createdBy?.name || "—",
    },
    {
      id: "createdAt",
      label: "Sent On",
      sort: true,
      render: (row) =>
        moment(row?.sentAt || row?.createdAt).format("DD-MM-YYYY hh:mm A"),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      align: "right",
      render: (row) => (
        <>
          <Tooltip title="View">
            <IconButton onClick={() => viewStageAction(row)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {permission.deletePermission && (
            <Tooltip title="Delete">
              <IconButton onClick={() => deleteStageAction(row)}>
                <DeleteIcon color={"error"} />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const getData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(type !== "All" && { type }),
      ...(source !== "All" && { source }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getSupplierNotificationList(value));
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  const handleSend = async (data, stopSubmitting) => {
    const payload = {
      title: data.title?.trim(),
      message: data.message?.trim(),
      type: data.type,
      productId: data.product?._id || null,
      // ALL | PRODUCT_RATE | SELECTED - the server resolves the actual vendor
      // list from this, so only SELECTED carries an explicit list.
      audience: data.audience,
      assignedSuppliers: (data.assignedSuppliers || []).map((s) => s._id),
    };
    const response = await dispatch(sendSupplierNotification(payload)).unwrap();
    stopSubmitting?.();
    if (response?.status === 200) {
      enqueueSnackbar(response.data?.responseMessage, { variant: "success" });
      getData();
      onClose();
    } else {
      enqueueSnackbar(
        response?.data?.responseMessage || "Could not send the notification",
        { variant: "error" },
      );
    }
  };

  const handleSaveSettings = async (data, stopSubmitting) => {
    const response = await dispatch(
      updateNotificationSettings({
        settings: (data.settings || []).map((row) => ({
          eventKey: row.eventKey,
          isEnabled: row.isEnabled !== false,
          title: row.title,
          messageTemplate: row.messageTemplate,
          type: row.type,
          notifyOnlyOnPriceChange: row.notifyOnlyOnPriceChange !== false,
        })),
      }),
    ).unwrap();
    stopSubmitting?.();
    if (response?.status === 200) {
      enqueueSnackbar(response.data?.responseMessage, { variant: "success" });
      closeSettings();
    } else {
      enqueueSnackbar(
        response?.data?.responseMessage || "Could not save the settings",
        { variant: "error" },
      );
    }
  };

  const DeleteNotification = () => {
    dispatch(deleteSupplierNotification(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, { variant: "success" });
          getData();
        } else {
          enqueueSnackbar(res.data?.responseMessage, { variant: "error" });
        }
        closeDelete();
      });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  return (
    <>
      {notificationData.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={type}
                        label="Type"
                        onChange={(e) => setType(e.target.value)}
                      >
                        <MenuItem value={"All"}>All</MenuItem>
                        {NOTIFICATION_TYPES.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={source}
                        label="Source"
                        onChange={(e) => setSource(e.target.value)}
                      >
                        <MenuItem value={"All"}>All</MenuItem>
                        <MenuItem value={"MANUAL"}>Manual</MenuItem>
                        <MenuItem value={"AUTO"}>Automatic</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>

          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Vendor Notifications
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Refresh">
                    <IconButton onClick={getData}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Notification Settings">
                    <IconButton onClick={openSettings}>
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={onOpen}
                      sx={{ mt: { xs: 1, md: 0 } }}
                      type="submit"
                      variant="contained"
                    >
                      Send Notification
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
              <Table
                columns={columns}
                rows={notificationData.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={notificationData.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>

          <DialogDefault
            open={isOpen}
            onClose={onClose}
            content={
              <SendNotification onClose={onClose} handleSubmit={handleSend} />
            }
            title="Send Notification"
            fullWidth={true}
            maxWidth={"sm"}
            actions={<></>}
          />
          <DialogDefault
            open={isSettingsOpen}
            onClose={closeSettings}
            content={
              <NotificationSettings
                onClose={closeSettings}
                handleSubmit={handleSaveSettings}
                canUpdate={permission.updatePermission}
              />
            }
            title="Notification Settings"
            fullWidth={true}
            maxWidth={"md"}
            actions={<></>}
          />
          <DialogDefault
            open={isView}
            onClose={closeView}
            content={
              <ViewNotification id={viewActionProps?._id} onClose={closeView} />
            }
            title="Notification Detail"
            fullWidth={true}
            maxWidth={"md"}
            actions={<></>}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete this notification?"
            onConfirm={DeleteNotification}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default Notification;
